import AppStoreCatalogStatus from '@/components/Credits/AppStoreCatalogStatus';
import { usesStoreBilling, isMacAppStore } from '@/lib/appDistribution';
import { safeAccessibilityRole } from '@/constants/utils/a11y';
import AutoTopupCheckoutOptIn from './AutoTopupCheckoutOptIn';
import { autoTopupSettingsForPack, canOfferAutoTopupForPack } from '@/lib/autoTopupOffer';
import { creditPacksApi } from '@/lib/api';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useAuthStore, useCreditPacksStore, useModalStore } from '@/store';
import useTheme from '@/hooks/useNeutralTheme';
import { PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import { getPlusCreditOptions } from '@/lib/plusCreditOptions';
import { formatCreditBalance } from '@/lib/formatCredits';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { getCreditPackIapProductId } from '@/hooks/useIosIap';
import PayWithBitcoinRow from '@/components/Credits/PayWithBitcoinRow';
import { WorkCreditReset } from '@/components/Coworker/WorkCreditRecoveryCard';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';

export default function WorkCreditShop({ onClose, requiredCredits }) {
  const { colors } = useTheme();
  const user = useAuthStore(s => s.user);
  const allPacks = useCreditPacksStore(s => s.creditPacks);
  const packs = isMacAppStore() ? allPacks.filter(pack => getCreditPackIapProductId(pack.credits)) : allPacks;
  const loading = useCreditPacksStore(s => s.isLoading);
  const fetchPacks = useCreditPacksStore(s => s.fetchCreditPacks);
  const { openModal } = useModalStore(s => s.actions);
  const { openCreditPacksCheckout, iap, usesWhopCheckout } = useGabPurchase();
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [purchased, setPurchased] = useState(false);
  const [autoOptIn, setAutoOptIn] = useState(false);
  const [autoNotice, setAutoNotice] = useState('');
  const [browserPending, setBrowserPending] = useState(false);
  const initialBalance = useRef(user?.credits?.totalAvailable ?? 0);
  const options = getPlusCreditOptions(user);
  const shortfall = Math.max(0, (requiredCredits || 0) - options.totalAvailable);
  const selected = packs.find(p => p.id === selectedId) || packs.find(p => p.credits >= shortfall) || packs[0];
  const canOptIn = canOfferAutoTopupForPack({ user, usesWhopCheckout, platform: Platform.OS, pack: selected });
  useEffect(() => { setAutoOptIn(false); }, [selected?.id, selected?.price, selected?.credits, user?.autoTopup?.version, user?.autoTopup?.paymentMethodFingerprint]);
  const added = options.totalAvailable > initialBalance.current;
  const load = useCallback(() => { fetchPacks().catch(() => setError('Credit packs couldn’t load. Please try again.')); }, [fetchPacks]);
  useEffect(() => { load(); }, [load]);
  const price = pack => {
    if (usesStoreBilling()) {
      const product = iap?.getProduct?.(getCreditPackIapProductId(pack.credits));
      return product?.localizedPrice || 'See store price';
    }
    return Number(pack.price).toLocaleString('en-US', { style: 'currency', currency: pack.currency || 'USD', maximumFractionDigits: 2, minimumFractionDigits: 0 });
  };
  const checkout = async () => {
    if (!selected || busy) return;
    const authorizedSettings = autoOptIn && canOptIn ? autoTopupSettingsForPack(selected, { version: user?.autoTopup?.version || 0, provider: usesWhopCheckout ? 'whop' : 'valmar', paymentMethodFingerprint: user?.autoTopup?.paymentMethodFingerprint }) : null;
    setBusy(true); setError('');
    try {
      const result = await openCreditPacksCheckout({
        creditPackId: selected.id, credits: selected.credits, autoTopupHandled: true,
        // Elements redirects the tab. Carry this explicit consent into the
        // confirmed return flow instead of relying on this in-memory promise.
        ...(authorizedSettings?.provider === 'whop' ? { autoTopupAuthorization: authorizedSettings } : {}),
      });
      if (Platform.OS === 'macos') setBrowserPending(!!result?.opened);
      if (result?.success) {
        setPurchased(true);
        if (authorizedSettings) {
          try {
            const response = await creditPacksApi.updateAutoTopupSettings({ ...authorizedSettings, afterPurchase: true, afterPurchasePaymentId: result.transactionId || result.receiptId });
            if (!response.autoTopup) throw new Error('Settings not confirmed');
            useAuthStore.getState().updateAutoTopupSettings(response.autoTopup);
            setAutoNotice('Auto top-up is enabled. You can manage it in Credits settings.');
          } catch (_) { setAutoNotice('Your purchase succeeded, but auto top-up could not be enabled. Review it in Credits settings.'); }
        }
        await useAuthStore.getState().fetchUserCredits({ force: true });
      }
    } catch (err) {
      if (!/checkout (?:closed|cancelled|canceled)|user closed|backdrop close/i.test(err?.message || '')) {
        setError('Checkout couldn’t open. Please try again. Your work is still saved.');
      }
    } finally { setBusy(false); }
  };
  const accent = colors.text_color_primary;
  return <View style={styles.container}>
    <Text size="xxl" weight="bold" style={styles.title}>{added ? 'Your credits are ready' : 'Pick up where you left off'}</Text>
    <Text size="s" color="secondary" style={styles.subtitle}>{added ? 'Your balance is updated. Head back to Work when you’re ready.' : 'Choose a one-time top-up. Your task stays right where you left it.'}</Text>
    <View style={[styles.balance, { backgroundColor: colors.solid_color_secondary }]}>
      <Text size="s" color="secondary">Your balance</Text><Text size="s" weight="semibold">{formatCreditBalance(options.totalAvailable)} credits</Text>
    </View>
    {added ? <Button text="Back to Work" prependIcon="arrow-left" variant="contrast" size="large" onPress={onClose} /> : <>
      <AppStoreCatalogStatus iap={iap} credits />
      <View style={styles.packs} accessibilityRole="radiogroup" accessibilityLabel="Credit packs">
        {packs.map(pack => {
          const active = selected?.id === pack.id;
          return <Pressable key={pack.id} accessibilityRole="radio" aria-checked={active} accessibilityState={{ checked: active, disabled: busy }} accessibilityLabel={`${formatCreditBalance(pack.credits)} credits for ${price(pack)}`} onPress={() => setSelectedId(pack.id)} disabled={busy}
            style={({ hovered }) => [styles.pack, { backgroundColor: active ? colors.solid_color_tertiary : colors.solid_color_primary, borderColor: active ? accent : colors.border_color_primary }, hovered && { borderColor: accent }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}><Text size="l" weight="semibold">{formatCreditBalance(pack.credits)}</Text><Icon name={active ? 'circle-check' : 'circle'} size={16} color={active ? accent : colors.text_color_tertiary} /></View>
            <Text size="xs" color="secondary">credits</Text>
            <Text size="m" weight="semibold" style={{ marginTop: 8 }}>{price(pack)}</Text>
          </Pressable>;
        })}
      </View>
      {loading && <ActivityIndicator color={accent} />}
      {!loading && !packs.length && <Button text="Reload credit packs" onPress={() => { useCreditPacksStore.setState({ isFetched: false }); load(); }} />}
      {!!selected && <>
        {shortfall > selected.credits && <Text size="xs" color="secondary">This pack won’t cover the next step. Choose at least {formatCreditBalance(shortfall)} credits.</Text>}
        {canOptIn && <AutoTopupCheckoutOptIn pack={selected} checked={autoOptIn} onChange={setAutoOptIn} disabled={busy} />}
        <Button text={busy ? 'Opening secure checkout…' : `Continue with ${formatCreditBalance(selected.credits)} credits · ${price(selected)}`} variant="contrast" size="large" accessibilityLabel={busy ? 'Opening secure checkout' : `Continue with ${formatCreditBalance(selected.credits)} credits · ${price(selected)}`} onPress={checkout} isLoading={busy} isDisabled={busy} />
        <Text size="xs" color="secondary" style={styles.subtitle}>One-time purchase · No subscription change</Text>
        {Platform.OS === 'macos' && <PayWithBitcoinRow creditPackId={selected.id} price={selected.price} disabled={busy} />}
      </>}
    </>}
    {!!autoNotice && <View accessibilityRole={safeAccessibilityRole('status')}><Text size="s" color="secondary">{autoNotice}</Text></View>}
    {!!error && <View accessibilityRole="alert"><Text size="s" color="secondary">{error}</Text></View>}
    {browserPending && !added && <Text size="s" color="secondary">Complete payment in your browser, then return here. If you already paid, wait for your balance to update before paying again.</Text>}
    {purchased && !added && <Text size="s" color="secondary">Payment completed. Your credits are being updated.</Text>}
    {!added && options.canUpgradeToPremium && <Pressable accessibilityRole="button" accessibilityLabel="Explore Plus Premium" onPress={() => { onClose?.(); openModal('PricingModal', { initialCadence: 'monthly' }); }} style={[styles.upgrade, { borderColor: colors.border_color_secondary }]}>
      <Icon name="sparkles" size={18} color={accent} /><View style={{ flex: 1, gap: 4 }}><Text size="s" weight="semibold">Make room for bigger projects</Text><Text size="xs" color="secondary">Plus Premium includes {PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY} credits every month.</Text><Text size="xs" weight="semibold" style={{ color: accent }}>Explore Plus Premium →</Text></View>
    </Pressable>}
    <WorkCreditReset user={user} />
    {!added && <Pressable onPress={onClose} accessibilityRole="button" accessibilityLabel="Back to Work" style={styles.back}><Text size="s" color="secondary" style={{ textDecorationLine: 'underline' }}>Back to Work</Text></Pressable>}
  </View>;
}

const styles = StyleSheet.create({
  container: { paddingHorizontal: 24, paddingBottom: 24, gap: 20 },
  title: { lineHeight: 32, letterSpacing: -0.5 },
  subtitle: { lineHeight: 20 },
  balance: { borderRadius: 10, padding: 12, flexDirection: 'row', justifyContent: 'space-between', gap: 8 },
  packs: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  pack: { flexGrow: 1, flexBasis: '28%', minWidth: 100, borderWidth: 1, borderRadius: 12, padding: 16 },
  upgrade: { borderWidth: 1, borderRadius: 12, padding: 15, flexDirection: 'row', gap: 12, alignItems: 'center' },
  back: { minHeight: 44, justifyContent: 'center', padding: 12, alignSelf: 'center' },
});
